import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { KaiProvider } from "@/contexts/KaiContext";
import { SkillThemeProvider } from "@/contexts/SkillThemeContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { FeedbackButton } from "@/components/FeedbackButton";
import { RepublicDayBanner } from "@/components/RepublicDayBanner";
import { GlobalKai } from "@/components/GlobalKai";
import { BetaDisclaimer } from "@/components/BetaDisclaimer";

// Lazy load all page components for code splitting
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const Quiz = lazy(() => import("./pages/Quiz"));
const FindMySkill = lazy(() => import("./pages/FindMySkill"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Profile = lazy(() => import("./pages/Profile"));
const UserProfile = lazy(() => import("./pages/UserProfile"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Chat = lazy(() => import("./pages/Chat"));
const ChatPage = lazy(() => import("./pages/ChatPage"));
const SkillCategory = lazy(() => import("./pages/SkillCategory"));
const LearnSkill = lazy(() => import("./pages/LearnSkill"));
const KaiStore = lazy(() => import("./pages/KaiStore"));
const SkillHome = lazy(() => import("./pages/SkillHome"));
const Portfolio = lazy(() => import("./pages/Portfolio"));
const Community = lazy(() => import("./pages/Community"));
const RoadmapPage = lazy(() => import("./pages/RoadmapPage"));
const LessonPage = lazy(() => import("./pages/LessonPage"));
const Missions = lazy(() => import("./pages/Missions"));
const SubmissionsHistory = lazy(() => import("./pages/SubmissionsHistory"));
const Notifications = lazy(() => import("./pages/Notifications"));

const queryClient = new QueryClient();

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <KaiProvider>
        <SkillThemeProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/chat" element={<Chat />} />
                  <Route path="/category/:category" element={<SkillCategory />} />
                  <Route path="/learn-skill/:skillId" element={<LearnSkill />} />
                  <Route path="/roadmap/:skillId" element={<RoadmapPage />} />
                  <Route path="/lesson/:lessonId" element={<LessonPage />} />
                  <Route path="/find-my-skill" element={<FindMySkill />} />
                  <Route path="/kai-store" element={<KaiStore />} />
                  <Route path="/skill-home" element={<SkillHome />} />
                  <Route path="/profile/:userId" element={<UserProfile />} />
                  <Route path="/user/:userId" element={<UserProfile />} />
                  <Route path="/portfolio" element={<ProtectedRoute><Portfolio /></ProtectedRoute>} />
                  <Route path="/community" element={<Community />} />
                  <Route path="/messages" element={<ChatPage />} />
                  <Route path="/missions" element={<Missions />} />
                  <Route path="/submissions" element={<ProtectedRoute><SubmissionsHistory /></ProtectedRoute>} />
                  <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
                  <Route path="/" element={<Index />} />
                  <Route path="/quiz" element={<ProtectedRoute><Quiz /></ProtectedRoute>} />
                  <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                  <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
                <GlobalKai />
              </Suspense>
              <RepublicDayBanner />
              <BetaDisclaimer />
              <FeedbackButton />
            </BrowserRouter>
          </TooltipProvider>
        </SkillThemeProvider>
      </KaiProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
